import SeeAll from '@space-core/components/seeAll'
import SwiperCards from '@space-shell/common/components/swiper'
import { AuthStatus, useAuthContext } from '@space-shell/common/contexts/auth'
import useRequest from '@space-shell/common/hooks/useRequest'
import type { SectionProductDetail } from '@space-shell/common/services/sections'
import { Section, SectionService, SectionsEndpoints } from '@space-shell/common/services/sections'

import { queryClient } from '@space-shell/config/request'
import { RoutesPaths } from '@space-shell/core/router'
import { useMembershipStore, useViewAsStore } from '@space-shell/core/store'
import ButtonsCallToActions from '@space-shell/modules/home/components/sectionsInShowcase/buttonsCallToActions'
import useGetProductDetails from '@space-shell/modules/home/hooks/useGetProductDetails/useGetProductDetails'
import { isSectionEnabledToShow } from '@space-shell/modules/home/utils/isSectionEnabledToShow'

import { SectionPlaceholder } from '@space-shell/remotes/preview/components/sectionPlaceholder'
import { useTranslation } from 'react-i18next'

import { generatePath, useNavigate } from 'react-router-dom'

import { AMOUNT_OF_CARDS, DynamicSections, ProductCardSections, ShowSeeALL } from './constants'
import ItemCard from './itemCard'

import ProductSectionTitle from '../sectionTitle'
import ShowcaseErrorState from '../showcaseErrorState'
import LoaderList from '../showcaseLoader/loaderList'

export type SectionProps = {
  section: Section
  isPreview?: boolean
  isMobile?: boolean
}

const Section = ({ section, isPreview, isMobile }: SectionProps) => {
  const { authStatus } = useAuthContext()
  const isUserAuthenticated = Boolean(authStatus === AuthStatus.AUTHENTICATED)
  const { membershipData } = useMembershipStore()
  const membershipId = membershipData?.id ?? ''
  const { role: viewAsRole, isEnabled: isViewAsEnabled } = useViewAsStore()
  const queryKeys = [String(viewAsRole), String(isUserAuthenticated), String(isViewAsEnabled)]
  const isDynamicSection = DynamicSections.includes(section.type)
  const isProductCard = ProductCardSections.includes(section.type)
  const {
    i18n: { language }
  } = useTranslation()
  const normalizedLanguage = String(language).toLowerCase()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { data: product = {} as SectionProductDetail, isInitialLoading: isLoadingProductDetails } =
    useGetProductDetails({
      enabled: Boolean(section.entityId),
      productId: section.entityId as string
    })

  const invalidateSectionItems = () =>
    queryClient.invalidateQueries([SectionsEndpoints.sectionsItems(membershipId, section.id)])

  const isSectionVisible = isSectionEnabledToShow(product)

  const {
    isError,
    data: sectionsItems,
    isLoading: isLoadingSectionItems
  } = useRequest([SectionsEndpoints.sectionsItems(membershipId, section.id), ...queryKeys], () =>
    SectionService.getSectionsItems(membershipId, section.id)
  )

  if (isLoadingSectionItems || isLoadingProductDetails) {
    return <LoaderList orientation={section.orientation} sectionType={section.type} title />
  }

  if (!isSectionVisible) {
    return null
  }

  if (isError) {
    return (
      <div className='_position-relative'>
        <ShowcaseErrorState
          loaderTitle
          onTryAgain={invalidateSectionItems}
          orientation={section.orientation}
        />
      </div>
    )
  }

  if (!sectionsItems?.content?.length && !isPreview) {
    return <div data-test='empty-section' />
  }

  if (!sectionsItems?.content?.length && isPreview) {
    return (
      <SectionPlaceholder
        type={section.type}
        title={section.title}
        orientation={section.orientation}
      />
    )
  }

  const handleSeeAll = () => {
    const productsRoute = generatePath(`${RoutesPaths.HOME}${RoutesPaths.PRODUCTS}`, {
      lang: normalizedLanguage,
      membership: membershipData?.slug
    })

    navigate(productsRoute)
  }

  const hasSeeAll =
    ShowSeeALL.includes(section.type) && sectionsItems.content.length > AMOUNT_OF_CARDS

  return (
    <div>
      <div className='_d-flex _align-items-center _justify-content-between'>
        <ProductSectionTitle
          product={product}
          isPreview={isPreview}
          sectionId={section.id}
          sectionType={section.type}
          productId={section.entityId}
          sectionTitle={section.title}
          isLoading={isLoadingProductDetails}
          isDynamicSection={isDynamicSection}
        />

        {hasSeeAll && (
          <SeeAll
            onClick={handleSeeAll}
            text={t('sections.see_all')}
            className='_text-3 _pr-4 _pr-md-10 _mb-5'
          />
        )}
      </div>

      <SwiperCards
        orientation={section.orientation}
        sectionType={section.type}
        {...(isProductCard && { slidesPerPage: AMOUNT_OF_CARDS })}>
        {sectionsItems?.content.map((item, index) => (
          <SwiperCards.Item key={item.itemId} virtualIndex={index}>
            <ItemCard
              item={item}
              section={section}
              data-test='item-card'
              isProductCard={isProductCard}
            />
          </SwiperCards.Item>
        ))}
      </SwiperCards>

      {isMobile && Boolean(section.entityId) && (
        <div className='_px-4 _mt-4'>
          <ButtonsCallToActions product={product} sectionId={section.id} isPreview={isPreview} />
        </div>
      )}
    </div>
  )
}

export default Section
